'use client';

import { useState, useCallback } from 'react';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import IconButton from '@mui/material/IconButton';
import {
  IVirtualCardsTableFilters,
  IVirtualCardsTableFilterValue,
} from '../../../../types/club-cards';

// ----------------------------------------------------------------------

type Props = {
  filters: IVirtualCardsTableFilters;
  onFilters: (name: string, value: IVirtualCardsTableFilterValue) => void;
  canReset: boolean;
  onReset: Function;
};

export default function TransactionHistoryTableToolbar({
  filters,
  onFilters,
  canReset,
  onReset,
}: Props) {
  const popover = usePopover();
  const handleFilterName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('name', event.target.value);
    },
    [onFilters]
  );

  const handleFilterStartDate = useCallback(
    (date: Date | null) => {
      onFilters('start', date);
    },
    [onFilters]
  );

  const handleFilterEndDate = useCallback(
    (date: Date | null) => {
      onFilters('end', date);
    },
    [onFilters]
  );

  return (
    <>
      <Stack
        spacing={2}
        width={1}
        alignItems={{ xs: 'flex-end', md: 'center' }}
        direction={{
          xs: 'column',
          md: 'row',
        }}
        sx={{
          p: 2.5,
          pr: { xs: 2.5, md: 1 },
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <FormControl
              sx={{
                flexShrink: 0,
                width: { xs: 1, md: 1 },
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="מתאריך"
                  format="DD/MM/YY"
                  value={filters.start}
                  onChange={handleFilterStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl
              sx={{
                flexShrink: 0,
                width: { xs: 1, md: 1 },
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="עד תאריך"
                  format="DD/MM/YY"
                  value={filters.end}
                  onChange={handleFilterEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
        </Grid>

        <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
          <TextField
            fullWidth
            value={filters.name}
            onChange={handleFilterName}
            placeholder="עד תאריך"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />

          <IconButton disabled={!canReset} onClick={() => onReset()} sx={{ cursor: 'pointer' }}>
            <Iconify icon="gg:trash" sx={{ color: canReset ? 'red' : 'gray' }} />
          </IconButton>

          {/* <IconButton onClick={popover.onOpen}> */}
          {/*  <Iconify icon="eva:more-vertical-fill" /> */}
          {/* </IconButton> */}
        </Stack>
      </Stack>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
          }}
        >
          <Iconify icon="solar:printer-minimalistic-bold" />
          Print
        </MenuItem>

        <MenuItem
          onClick={() => {
            popover.onClose();
          }}
        >
          <Iconify icon="solar:import-bold" />
          Import
        </MenuItem>

        <MenuItem
          onClick={() => {
            popover.onClose();
          }}
        >
          <Iconify icon="solar:export-bold" />
          Export
        </MenuItem>
      </CustomPopover>
    </>
  );
}
